/**
 * The rules every schema-driven JSONB editor shares — the player's Stats and
 * Meta cards and the guild's Metadata card.
 *
 * They lived as three copies of the same few lines, which is fine until the
 * rule moves: "whole number" is a contract those three cards make with the
 * operator, and the next time it is refined (a leading zero, a wider range) the
 * copies are exactly what would drift apart.
 */

import type { SchemaField } from "../components/SchemaEditor/SchemaEditor";
import type { Option } from "../components/Select/Select";

/**
 * True when `s` is a whole number — the only client-side check an `int` field
 * makes. Safe-integer bounded, because anything past 2^53 has already lost
 * digits by the time JSON.parse hands it over, and a form must not pretend it
 * can round-trip a value it cannot.
 */
export function isWholeNumber(s: string): boolean {
  const t = s.trim();
  if (t === "") return false;
  if (!/^[+-]?\d+$/.test(t)) return false;
  return Number.isSafeInteger(Number(t));
}

/** Pretty-print a JSONB bag the way a Raw JSON textarea shows it. */
export function prettyJson(obj: Record<string, unknown>): string {
  return JSON.stringify(obj, null, 2);
}

/**
 * The row that stands for "no key at all". Only the fields where an unset key
 * is a WORKING configuration offer it — see schemaKeyOptionsWithUnset.
 */
const NOT_SET_OPTION: Option = { value: "", label: "— not set —" };

/**
 * The `int` fields of an already declared schema, as <Select> options, so a
 * stat key is picked rather than typed and cannot be misspelled.
 *
 * Two edge cases shape this list. The schema arrays are free-form JSONB the
 * backend never rewrites, so entries can be junk — anything that isn't an
 * `int` field with a non-empty key is skipped. And `selected` may name a field
 * that was since deleted from the schema: it is kept as an option so a stored
 * config doesn't silently lose the key the moment the row renders.
 *
 * This variant has no "unset" row, and is the one for a REQUIRED key: an empty
 * value there is not a configuration but a field nobody filled in, and
 * <Select> already has a branch for that — its `placeholder`. Offering a list
 * entry the form is going to refuse would be inviting the click.
 */
export function schemaKeyOptions(
  schema: SchemaField[],
  selected: string,
): Option[] {
  const out: Option[] = [];
  const seen = new Set<string>();
  for (const field of Array.isArray(schema) ? schema : []) {
    if (!field || typeof field !== "object") continue;
    if (typeof field.key !== "string" || field.key === "") continue;
    if (field.type !== "int") continue;
    if (seen.has(field.key)) continue;
    seen.add(field.key);
    out.push({ value: field.key, label: field.key, mono: true, meta: "int" });
  }
  if (selected !== "" && !seen.has(selected)) {
    out.push({ value: selected, label: selected, mono: true });
  }
  return out;
}

/**
 * True when `schemaKeyOptions` would come back empty — there is no `int` field
 * for this key to name, and no list to open.
 *
 * It asks about the LIST, `selected` included, which is not quite the question
 * a required key asks ("has this project declared anything at all", where a
 * stored key must not be allowed to answer yes — see LeaderboardConfigForm's
 * `noIntStat`, which passes `""` on purpose). Here a stored key is a row the
 * operator can still pick or clear, so while there is one the picker works.
 */
export function noIntStatKey(
  schema: SchemaField[],
  selected: string,
): boolean {
  return schemaKeyOptions(schema, selected).length === 0;
}

/**
 * The same list, led by the "— not set —" sentinel: for the keys where having
 * none is a complete, working configuration (`guild.succession_level_key`, the
 * recommendation requirement pair), so clearing one is a choice the list can
 * express rather than something only an empty trigger hints at.
 */
export function schemaKeyOptionsWithUnset(
  schema: SchemaField[],
  selected: string,
): Option[] {
  return [NOT_SET_OPTION, ...schemaKeyOptions(schema, selected)];
}
